/* prettier-ignore-file */
/**
 * Wizard step 5 — review and finish.
 *
 * Staff (isAuthenticated) get the three manual-booking modes carried over from
 * NewBooking.js:4945-5000: sign & pay, bypass waiver, or book directly with no
 * payment. Regular customers never see them — they get a single button into the
 * waiver and the normal payment flow, matching the gating at NewBooking.js:4920.
 */

import React from "react";
import { View, Text, StyleSheet, ScrollView, Switch } from "react-native";
import { formatCentsAsUSD } from "../../utils/formatting";
import Button from "../ui/Button";

/**
 * The three modes used to render as three differently-coloured submit buttons
 * (blue / orange / green). The selected mode is already conveyed by the switches
 * and the button label, so the colour was decoration rather than information --
 * exactly the "multicoloured buttons" problem. One primary button now.
 */
const MODES = [
  {
    key: "signPay",
    label: "Sign waiver & pay",
    hint: "Standard flow — waiver, then payment.",
    cta: (waiverSigned) => (waiverSigned ? "Complete Payment" : "Sign Waiver"),
  },
  {
    key: "signPayCash",
    label: "Sign waiver & pay with cash",
    hint: "Waiver, then the cash desk screen to take payment at the register.",
    cta: () => "Sign Waiver & Pay Cash",
  },
  {
    key: "bypassWaiver",
    label: "Bypass waiver (direct to payment)",
    hint: "Skips the waiver and goes straight to Stripe.",
    cta: () => "Start Booking Flow",
  },
  {
    key: "direct",
    label: "Book directly (no payment or waiver)",
    hint: "Creates a confirmed booking immediately.",
    cta: () => "Create Manual Booking",
  },
];

const StepReview = ({
  isAuthenticated,
  quote,
  draft,
  products1,
  products2,
  product1Title,
  product2Title,
  manualBookingMode,
  onModeChange,
  onSubmit,
  submitting,
  missingFields,
  cancellationPolicy,
}) => {
  const canSubmit = missingFields.length === 0 && !submitting;
  const activeMode = MODES.find((m) => m.key === manualBookingMode) || MODES[0];

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  const line = (label, valueCents, options = {}) => (
    <View style={styles.summaryRow} key={label}>
      <Text style={[styles.summaryLabel, options.muted && styles.summaryMuted]}>{label}</Text>
      <Text
        style={[
          styles.summaryValue,
          options.muted && styles.summaryMuted,
          options.discount && styles.summaryDiscount,
        ]}
      >
        {options.discount
          ? `−${formatCentsAsUSD(Math.abs(valueCents))}`
          : formatCentsAsUSD(valueCents)}
      </Text>
    </View>
  );

  const selectedBikeEntries = Object.entries(draft.selectedBikes || {}).filter(([, q]) => q > 0);
  const selectedAddOnEntries = Object.entries(draft.selectedAddOns || {}).filter(
    ([, v]) => v === true || v?.quantity > 0
  );

  return (
    <ScrollView style={styles.container}>
      {/* --- Order summary --- */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Your booking</Text>

        <View style={styles.datesRow}>
          <Text style={styles.datesText}>
            {formatDate(draft.startDate)} → {formatDate(draft.endDate)}
          </Text>
          <Text style={styles.daysBadge}>
            {quote.days} {quote.days === 1 ? "day" : "days"}
          </Text>
        </View>

        <Text style={styles.groupLabel}>{product1Title}</Text>
        {selectedBikeEntries.length === 0 ? (
          <Text style={styles.emptyText}>None selected</Text>
        ) : (
          selectedBikeEntries.map(([name, quantity]) => (
            <Text key={name} style={styles.itemText}>
              {quantity}× {name}
            </Text>
          ))
        )}

        {selectedAddOnEntries.length > 0 && (
          <>
            <Text style={styles.groupLabel}>{product2Title}</Text>
            {selectedAddOnEntries.map(([id, value]) => {
              const product = (products2 || []).find((p) => p.id === id);
              const quantity = value === true ? 1 : value?.quantity || 0;
              return (
                <Text key={id} style={styles.itemText}>
                  {quantity}× {product?.name || id}
                </Text>
              );
            })}
          </>
        )}

        {/* BOTH legs, always, each on its own row. The old single joined label
            collapsed to just "Delivery" when only the outbound leg was paid, so a
            customer who chose delivery saw nothing telling them they still had to
            bring everything back to the shop themselves -- precisely the mistake
            this step exists to catch. Wording mirrors the pickers in
            StepTransportation ("Getting it to you" / "Getting it back") so each
            answer is recognisable as the one they gave, and the "We"/"You" prefix
            plus colour says at a glance whose job each leg is. */}
        <Text style={styles.groupLabel}>Transportation</Text>
        <View style={styles.legRow}>
          <Text style={styles.legLabel}>Getting it to you</Text>
          <Text style={[styles.legValue, draft.deliveryNeeded && styles.legValueService]}>
            {draft.deliveryNeeded ? "🚚 We deliver to you" : "🏬 You pick up at the shop"}
          </Text>
        </View>
        <View style={styles.legRow}>
          <Text style={styles.legLabel}>Getting it back</Text>
          <Text style={[styles.legValue, draft.homePickupNeeded && styles.legValueService]}>
            {draft.homePickupNeeded ? "🚚 We collect from you" : "🏬 You drop off at the shop"}
          </Text>
        </View>
        {draft.addressInput ? <Text style={styles.addressText}>{draft.addressInput}</Text> : null}
        {draft.miles != null && <Text style={styles.itemText}>{draft.miles} miles each way</Text>}
      </View>

      {/* --- Cost breakdown --- */}
      <View style={[styles.section, styles.totalSection]}>
        <Text style={styles.sectionTitle}>Total</Text>

        {line(product1Title, quote.product1Cents)}
        {quote.product2Cents > 0 && line(product2Title, quote.product2Cents)}
        {quote.durationDiscountCents > 0 &&
          line(quote.durationDiscountName || "Duration discount", quote.durationDiscountCents, {
            discount: true,
          })}
        {quote.deliveryCostCents > 0 && line("Delivery", quote.deliveryCostCents)}
        {quote.pickupCostCents > 0 && line("Home pickup", quote.pickupCostCents)}
        {quote.transportationDiscountCents > 0 &&
          line(
            quote.transportationDiscountName || "Transportation discount",
            quote.transportationDiscountCents,
            { discount: true }
          )}
        {quote.platformFeeCents > 0 && line("Booking fee", quote.platformFeeCents, { muted: true })}
        {quote.salesTaxCents > 0 &&
          line(`Sales tax (${quote.salesTaxPercent}%)`, quote.salesTaxCents, { muted: true })}
        {quote.cardProcessingFeeCents > 0 &&
          line(`Card processing (${quote.cardProcessingFeePercent}%)`, quote.cardProcessingFeeCents, {
            muted: true,
          })}

        <View style={styles.totalDivider} />
        <View style={styles.summaryRow}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalValue}>{formatCentsAsUSD(quote.finalTotalCents)}</Text>
        </View>
      </View>

      {/* --- Blockers --- */}
      {missingFields.length > 0 && (
        <View style={styles.missingBox}>
          <Text style={styles.missingTitle}>⚠️ Still needed</Text>
          {missingFields.map((field) => (
            <Text key={field} style={styles.missingItem}>
              • {field}
            </Text>
          ))}
        </View>
      )}

      {cancellationPolicy ? (
        <View style={styles.policyBox}>
          <Text style={styles.policyTitle}>Cancellation policy</Text>
          <Text style={styles.policyText}>{cancellationPolicy}</Text>
        </View>
      ) : null}

      {/* --- Finish --- */}
      {isAuthenticated ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Manual booking controls</Text>
          {MODES.map((mode) => (
            <View key={mode.key} style={styles.modeRow}>
              <View style={styles.modeTextBlock}>
                <Text style={styles.modeLabel}>{mode.label}</Text>
                <Text style={styles.modeHint}>{mode.hint}</Text>
              </View>
              <Switch
                value={manualBookingMode === mode.key}
                onValueChange={(val) => val && onModeChange(mode.key)}
              />
            </View>
          ))}

          <Button
            title={activeMode.cta(draft.waiverSigned)}
            onPress={onSubmit}
            disabled={!canSubmit}
            loading={submitting}
            fullWidth
            style={styles.submitSpacing}
          />
        </View>
      ) : (
        <View style={styles.section}>
          <Button
            title={draft.waiverSigned ? "Continue to payment" : "Sign waiver & continue"}
            onPress={onSubmit}
            disabled={!canSubmit}
            loading={submitting}
            fullWidth
          />
          <Text style={styles.customerHint}>
            You'll sign the rental waiver next, then pay securely through Stripe.
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  section: { backgroundColor: "#FFFFFF", borderRadius: 8, padding: 16, marginBottom: 12 },
  totalSection: { borderWidth: 2, borderColor: "#0E7C6B" },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2E3A38", marginBottom: 12 },
  emptyText: { fontSize: 13, color: "#8A9794" },

  datesRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  datesText: { fontSize: 14, color: "#2E3A38", fontWeight: "500" },
  daysBadge: {
    fontSize: 12,
    color: "#0E7C6B",
    backgroundColor: "#E3F2EF",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  groupLabel: { fontSize: 11, color: "#8A9794", marginTop: 12, marginBottom: 4, textTransform: "uppercase" },
  itemText: { fontSize: 14, color: "#2E3A38", paddingVertical: 1 },
  addressText: { fontSize: 13, color: "#5C6B68", paddingVertical: 1 },

  // Two-column leg rows. Green marks a leg the shop handles, ink marks one the
  // customer has to do themselves -- the distinction the old label lost.
  legRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: 3,
  },
  legLabel: { fontSize: 13, color: "#5C6B68", paddingRight: 12 },
  legValue: {
    fontSize: 14,
    fontWeight: "700",
    color: "#2E3A38",
    flexShrink: 1,
    textAlign: "right",
  },
  legValueService: { color: "#1F8A5B" },

  summaryRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 5 },
  summaryLabel: { fontSize: 13, color: "#2E3A38", flex: 1, paddingRight: 8 },
  summaryValue: { fontSize: 13, color: "#2E3A38", fontWeight: "500" },
  summaryMuted: { color: "#5C6B68" },
  summaryDiscount: { color: "#1F8A5B" },
  totalDivider: { height: 1, backgroundColor: "#DDE3E2", marginVertical: 10 },
  totalLabel: { fontSize: 16, fontWeight: "bold", color: "#2E3A38" },
  totalValue: { fontSize: 20, fontWeight: "bold", color: "#0E7C6B" },

  missingBox: {
    backgroundColor: "#FDF3E3",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  missingTitle: { fontSize: 14, fontWeight: "bold", color: "#2E3A38", marginBottom: 6 },
  missingItem: { fontSize: 13, color: "#2E3A38", paddingVertical: 1 },

  policyBox: { backgroundColor: "#FFFFFF", borderRadius: 8, padding: 16, marginBottom: 12 },
  policyTitle: { fontSize: 13, fontWeight: "bold", color: "#2E3A38", marginBottom: 6 },
  policyText: { fontSize: 12, color: "#5C6B68", lineHeight: 17 },

  modeRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  modeTextBlock: { flex: 1, paddingRight: 12 },
  modeLabel: { fontSize: 14, color: "#2E3A38", fontWeight: "500" },
  modeHint: { fontSize: 11, color: "#8A9794", marginTop: 2 },

  submitSpacing: { marginTop: 16 },
  customerHint: { fontSize: 12, color: "#8A9794", textAlign: "center", marginTop: 10 },
});

export default StepReview;
